import React, { useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { Outlet } from 'react-router-dom';
import { STORAGE_KEYS, remove, set } from '../../utils/storage';
import { loginScopes } from '../../config/config';

export const RequiredAuth = () => {
  const { instance, accounts, inProgress } = useMsal();

  const acquireAccessToken = useCallback(async () => {
    if (!accounts[0]) return;

    try {
      // For Entra authentication, we'll use the ID token instead of access token
      // ID tokens are automatically available after login and contain user identity claims
      const tokenRequest = {
        scopes: loginScopes,
        account: accounts[0],
        forceRefresh: false
      };

      const response = await instance.acquireTokenSilent(tokenRequest);

      // Use ID token for backend authentication since it contains user identity
      // and doesn't require additional consent
      const tokenForBackend = response.idToken || response.accessToken;
      
      if (!tokenForBackend) {
        throw new Error('No suitable token found in response');
      }

      // Store token in the format expected by base.service.js
      set('msal.token.storage', {
        accessToken: tokenForBackend, // Use ID token as the "access token" for our backend
        expiresOn: response.expiresOn,
        scopes: response.scopes,
        tokenType: response.idToken ? 'id_token' : 'access_token'
      });
      
    } catch (error) {
      // If silent token acquisition fails, try interactive flow
      if (error.name === 'InteractionRequiredAuthError') {
        try {
          await instance.acquireTokenRedirect({
            scopes: loginScopes,
            account: accounts[0]
          });
        } catch (interactiveError) {
          console.error('Interactive token acquisition failed:', interactiveError);
        }
      }
    }
  }, [instance, accounts]);

  useEffect(() => {
    // Wait for MSAL to finish initializing
    if (inProgress === "startup") {
      return;
    }

    // If any interaction is in progress, wait for it to complete
    if (inProgress !== "none") {
      return;
    }

    // Only redirect if we have no accounts AND no interaction is in progress
    if (accounts.length === 0) {
      // Store the current URL before redirecting to login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      
      // No authenticated user, redirect to login
      remove(STORAGE_KEYS.USER_CONFIGURATION);
      
      instance.loginRedirect({
        scopes: loginScopes,
        redirectUri: window.location.origin + '/login/callback',
        postLogoutRedirectUri: window.location.origin,
        state: currentUrl // Store the original URL in the state
      }).catch((error) => {
        console.error("Login redirect failed:", error);
      });
    } else {
      // User is authenticated, acquire access token for API calls
      acquireAccessToken();
    }
  }, [instance, accounts, inProgress, acquireAccessToken]);

  // Show loading while MSAL is working or we have no accounts
  if (inProgress !== "none" || accounts.length === 0) {
    return (
      <div>
        <h1>Loading...</h1>
        <p>Authenticating...</p>
      </div>
    );
  }

  return <Outlet />;
};
